import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useHistory, useLocation } from 'react-router-dom';

import { Switch } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import { useJsApiLoader } from '@react-google-maps/api';
import clsx from 'clsx';
import { FastField, Field, FieldProps, Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';

import { useEditManualSupplierMutation } from '../../../api/company';
import { ContactType } from '../../../api/company/types';
import { useGetMySupplierById } from '../../../api/supplier/hooks';
import SvgUploadPhoto from '../../../assets/icons/UploadPhoto';
import Logo from '../../../assets/images/logo/Logo_White.svg';
import { BlurredImage } from '../../../shared/components/blurred-image';
import { BusinessPlaceAutocomplete } from '../../../shared/components/business-place-autocomplete';
import { ThemedButton } from '../../../shared/components/themed-button';
import { EditIcon } from '../../../shared/components/edit-button';
import { FormikInput } from '../../../shared/components/formik-input';
import { ImageUpload } from '../../../shared/components/image-upload';
import { OverlayModal } from '../../../shared/components/overlay-modal';
import { CustomTimePicker } from '../../../shared/components/time-picker';
import { week } from '../../../shared/constants/week';
import { useScreenSize } from '../../../shared/hooks/use-screen-size';
import { ToastService } from '../../../shared/services/toastService';
import { useAppDispatch, useAppSelector } from '../../../store';
import { getIsImpersonated } from '../../../store/user';
import { backToAdminThunk } from '../../../store/user/user.actions';

import { useRippleStyles } from '../../../styles/customRipple';
import { useStyles } from './style';
import { useCurrentTheme } from '../../../api/admin-themes/hooks';
import { getCurrency } from '../../../shared/helpers/getCurrency';
import DoneIcon from '@material-ui/icons/Done';
import { dateToString, stringToDate } from '../../../shared/helpers/stringHelper';

interface Values {
  business: string;
  ordersEmail?: string;
  phone?: string;
  freeDeliveryAt?: string;
  deliveryFee?: string;
  cutOffTime?: string;
}

const EditManualSupplier: React.FC = () => {
  const classes = useStyles();
  const rippleClass = useRippleStyles();
  const { isMobile } = useScreenSize();
  const { goBack } = useHistory();
  const { id } = useParams<{ id: string }>();
  const { state: navState } = useLocation<{ setup: boolean } | null>();
  const supplierId = parseFloat(id) as number;
  const dispatch = useAppDispatch();
  const { logoUrl } = useCurrentTheme();

  const supplier = useGetMySupplierById(supplierId, true);
  const { email, contactId, officeId, phone } = useMemo(() => {
    // const supplierContact = supplier?.company_contacts?.find((el) => el.is_order_email);
    const supplierContact = supplier?.company_contacts?.find((el) => el.contact_type === ContactType.ORDER);
    const supplierOfficeContact = supplier?.company_contacts?.find((el) => el.contact_type === ContactType.OFFICE);
    return {
      email: supplierContact?.email || '',
      contactId: supplierContact?.id,
      phone: supplierOfficeContact?.phone_number || '',
      officeId: supplierOfficeContact?.id,
    };
  }, [supplier]);

  const [isOrderEmail, setIsOrderEmail] = useState(true);
  const [isImageUploadShown, setIsImageUploadShown] = useState(false);
  const [logo, setLogo] = useState<Blob | null>(null);
  const [selectedDays, setSelectedDays] = useState<number[]>([]);

  const [editManual, { isLoading: editLoading }] = useEditManualSupplierMutation();
  const impersonated = useAppSelector(getIsImpersonated);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string,
    libraries: ['places'],
  });

  const businessSchema = Yup.object().shape({
    business: Yup.string().required('Required field'),
    ordersEmail: Yup.string().email('Please enter a valid email'),
  });

  const onBackToAdmin = () => {
    dispatch(backToAdminThunk());
  };

  const onSetDeliveryDay = (day: number) => {
    if (selectedDays.includes(day)) {
      setSelectedDays((state) => state.filter((item) => item !== day));
    } else {
      setSelectedDays((state) => [...state, day]);
    }
  };

  const onSubmit = async (values: Values, formikHelpers: FormikHelpers<any>) => {
    formikHelpers.setSubmitting(false);
    if (!supplierId) {
      return;
    }

    const reqBody = new FormData();
    reqBody.append('supplier_id', supplierId.toString());
    logo && reqBody.append('company[picture]', logo);
    reqBody.append('company[name]', values.business);
    reqBody.append(`company[contacts_attributes][0][contact_type]`, ContactType.ORDER);
    contactId && reqBody.append(`company[contacts_attributes][0][id]`, contactId.toString());
    reqBody.append(`company[contacts_attributes][0][email]`, isOrderEmail ? values.ordersEmail || '' : '');
    reqBody.append('company[contacts_attributes][0][is_order_email]', 'true');
    reqBody.append(`company[contacts_attributes][1][contact_type]`, ContactType.OFFICE);
    officeId && reqBody.append(`company[contacts_attributes][1][id]`, officeId.toString());
    reqBody.append(`company[contacts_attributes][1][phone_number]`, values.phone as string);

    supplier?.default_freight_rule.id && reqBody.append('company[freight_rule_attributes][id]', supplier?.default_freight_rule.id.toString());
    reqBody.append('company[freight_rule_attributes][default_delivery_fee]', 
      values.deliveryFee ? values.deliveryFee : '0'
    );
    reqBody.append('company[freight_rule_attributes][default_order_cut_off_time]', values.cutOffTime || '23:59');
    reqBody.append('company[freight_rule_attributes][free_delivery_threshold]', 
      values.freeDeliveryAt ? values.freeDeliveryAt : '0',
    );
    reqBody.append('company[freight_rule_attributes][enable_free_delivery]',
      !!(values.freeDeliveryAt && parseFloat(values.freeDeliveryAt) > 0 && values.deliveryFee && parseFloat(values.deliveryFee) > 0) ? 'true' : 'false' );

    reqBody.append('company[connection_detail_attributes][id]', supplier?.connection_detail?.id ? supplier.connection_detail.id.toString() : '');
    reqBody.append('company[connection_detail_attributes][delivery_days]', selectedDays.join(','));
    await editManual(reqBody);
    ToastService.success(`${values.business} has been updated`);
    goBack();
  };

  const onUploadLogoClick = () => {
    setIsImageUploadShown(true);
  };

  const onClearImage = () => {
    setIsImageUploadShown(false);
    setLogo(null);
  };

  useEffect(() => {
    setSelectedDays(supplier?.connection_detail?.delivery_days || []);
  }, [supplier]);

  return (
    <div className={classes.container}>
      <Formik
        initialValues={{
          business: supplier?.company.name || '',
          ordersEmail: email,
          phone,
          cutOffTime: supplier?.default_freight_rule.default_order_cut_off_time || '23:59',
          deliveryFee: (supplier?.default_freight_rule.default_delivery_fee || 0).toString(),
          freeDeliveryAt: (supplier?.default_freight_rule.free_delivery_threshold || 0).toString()
        }}
        onSubmit={onSubmit}
        validationSchema={businessSchema}
        enableReinitialize={true}
      >
        {({ submitForm, setFieldValue, values }) => (
          <Form className={classes.root}>
            <div className={classes.logoBox}>
              <img src={logoUrl || Logo} alt='OpenPantry' className={classes.logo} style={{ objectFit: 'contain', maxHeight: 70 }} />
            </div>
            <h2 className={classes.title}>{navState?.setup ? 'Complete Supplier setup' : 'Edit Supplier'}</h2>
            <div className={classes.businessRow}>
              {isLoaded && (
                <div className={classes.fieldWrap}>
                  <BusinessPlaceAutocomplete
                    placeholder='Search for business...'
                    setPlace={(place: google.maps.places.PlaceResult) => {
                      setFieldValue('business', place.name || '');
                    }}
                    label='Business Name*'
                    initialValue={values.business}
                    error={!values.business}
                  />
                </div>
              )}
              <div className={clsx([classes.imgWrap, rippleClass.ripple])} onClick={onUploadLogoClick}>
                {supplier?.company?.picture_url || logo ? (
                  <>
                    <div className={classes.addPhotoBg}>
                      <EditIcon />
                    </div>
                    <BlurredImage src={logo ? URL.createObjectURL(logo) : supplier?.company?.picture_url || ''} />
                  </>
                ) : (
                  <div className={classes.uploadPhoto}>
                    <SvgUploadPhoto />
                    {isMobile ? 'Upload' : 'Upload logo'}
                  </div>
                )}
              </div>
            </div>
            <div className={classes.fieldWrap}>
              <Field name='phone'>
                {(fieldProps: FieldProps) => <FormikInput {...fieldProps} label='Contact Phone Number' placeholder='Phone Number' />}
              </Field>
            </div>
            <div className={classes.fieldWrap}>
              <div className={classes.orderEmailWrap}>
                <span>SEND orders as a purchase order email?</span>
                <Switch
                  checked={isOrderEmail}
                  onChange={() => setIsOrderEmail(!isOrderEmail)}
                  color='primary'
                  classes={{
                    thumb: classes.thumb,
                    track: classes.track,
                    checked: classes.checked,
                  }}
                />
              </div>
              {isOrderEmail && (
                <Field name='ordersEmail'>
                  {(fieldProps: FieldProps) => <FormikInput {...fieldProps} placeholder='Email address' autoComplete='on' />}
                </Field>
              )}
              <div className={classes.ordersEmailNote}>
                {isOrderEmail ? 'Orders will be sent as a purchase order to this email address' : 'No orders will be sent'}
              </div>
            </div>
            <div className={classes.deliveryBox}>
              <div className={classes.deliveryTitle}>Delivery Information</div>
              <div className={classes.deliverySubtitle}>Select the days this supplier delivers</div>
              <div className={classes.weekDaysBox}>
                {week.map(({ title, key, id }) => (
                  <div
                    key={key}
                    className={clsx({
                      [classes.weekDayItem]: true,
                      [classes.weekDayItemSelected]: selectedDays.includes(id),
                    })}
                    onClick={onSetDeliveryDay.bind(null, id)}
                  >
                    <CheckIcon className={classes.checkIcon} />
                    {title}
                  </div>
                ))}
              </div>
              <div className={classes.deliveryDetailsRow}>
                <div>
                  <div className={classes.cutOffLabel}>Order cut off time</div>
                  <FastField name='cutOffTime'>
                    {({ field, form }: FieldProps) => (
                      <CustomTimePicker
                        time={stringToDate(field.value)}
                        onSetTime={(date: Date | null) => {
                          const timeStr = dateToString(date);
                          form.setFieldValue(field.name, timeStr);
                        }}
                      />
                    )}
                  </FastField>
                </div>
                <div>
                  <Field name='deliveryFee'>
                    {(fieldProps: FieldProps) => <FormikInput {...fieldProps}
                      inputProps={{
                        startAdornment: <span>{getCurrency()}</span>,
                      }}
                      label='Delivery Fee' placeholder='0$' type='number'
                    />}
                  </Field>
                </div>
                <div>
                  <Field name='freeDeliveryAt'>
                    {(fieldProps: FieldProps) => <FormikInput {...fieldProps}
                      className={classes.enableFreeFor}
                      inputProps={{
                        startAdornment: <span>{getCurrency()}</span>,
                      }}
                      label='Enable free delivery for' placeholder='0' type='number' />}
                  </Field>
                </div>
              </div>
              <div className={classes.enableFreeRow}>
                <div className={clsx(classes.enableFree, parseFloat(values.freeDeliveryAt) > 0 && parseFloat(values.deliveryFee) > 0 && classes.enableFreeTick)}><DoneIcon /></div>
                Enable free delivery for orders over: {getCurrency()}{values.freeDeliveryAt}
              </div>
            </div>
            <div className={classes.btnsBlock}>
              <ThemedButton onClick={goBack} title={'Back'} buttonStyle='secondary' width={215} disabled={editLoading} />
              <ThemedButton onClick={submitForm} title={'Save'} width={215} disabled={!values.business || editLoading} />
            </div>
          </Form>
        )}
      </Formik>
      {impersonated && (
        <ThemedButton
          onClick={onBackToAdmin}
          title='Admin View'
          buttonStyle='greenTransparent'
          isSmall={true}
          customClass={classes.backToAdmin}
          width={140}
        />
      )}
      <OverlayModal isOpen={isImageUploadShown} onClose={setIsImageUploadShown.bind(null, false)}>
        <>
          <div className={classes.uploadTitle}>Upload Supplier logo</div>
          <div className={classes.uploadBox}>
            <ImageUpload
              saveImage={(picture) => {
                setLogo(picture);
                setIsImageUploadShown(false);
              }}
              closeModal={onClearImage}
              skipCloseOnSave={true}
              tipElement={
                <ThemedButton
                  customClass={classes.cancelUpload}
                  onClick={onClearImage}
                  title={'Cancel'}
                  width={150}
                  buttonStyle='secondary'
                />
              }
            />
          </div>
        </>
      </OverlayModal>
    </div>
  );
};

export default EditManualSupplier;
